'use client';

import type { CategoryData } from 'src/types/category-data';
import { useMemo } from 'react';
import { Checkbox } from '@savvy-kit/components/checkbox';
import { DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator } from '@savvy-kit/components/dropdown-menu';
import { isNil } from 'es-toolkit';
import { Search, X } from 'lucide-react';

import { CategoryIndicator } from '../../tags/category-indicator';

export type CategoriesFilterContentProps = {
  readonly allCategories: readonly CategoryData[];
  readonly selectedCategoryIds: readonly string[];
  readonly onToggleCategory: (categoryId: string) => void;
  readonly onClearAll: () => void;
  readonly search: string;
  readonly onSearchChange: (value: string) => void;
  readonly searchInputRef?: React.RefObject<HTMLInputElement | null>;
  readonly onEnterKeyDown?: () => void;
  readonly useDropdownContext?: boolean;
};

function CategoriesFilterContentBase({
  allCategories,
  selectedCategoryIds,
  onToggleCategory,
  onClearAll,
  search,
  onSearchChange,
  searchInputRef,
  onEnterKeyDown,
  useDropdownContext,
}: CategoriesFilterContentProps & { readonly useDropdownContext: boolean }) {
  // Filter categories to only show those with at least one tag with householdCount > 0
  const availableCategories = useMemo(() => {
    return allCategories.filter((category) => {
      return category.organizationHouseholdTags.some((tag) => tag.householdCount > 0);
    });
  }, [allCategories]);

  const filteredCategories = useMemo(() => {
    return availableCategories.filter(
      (category) => !isNil(category.name) && category.name.toLowerCase().includes(search.toLowerCase()),
    );
  }, [availableCategories, search]);

  const selectedCategories = useMemo(() => {
    // Filter selected categories and order them by selection order
    const selected = filteredCategories.filter((category) => selectedCategoryIds.includes(category.id));
    // Create a map for quick lookup of selection index
    const selectionOrderMap = new Map(selectedCategoryIds.map((id, index) => [id, index]));
    // Sort by selection order
    return selected.sort((a, b) => {
      const orderA = selectionOrderMap.get(a.id) ?? Infinity;
      const orderB = selectionOrderMap.get(b.id) ?? Infinity;
      return orderA - orderB;
    });
  }, [filteredCategories, selectedCategoryIds]);

  const unselectedCategories = useMemo(() => {
    // Filter unselected categories and sort alphabetically
    const unselected = filteredCategories.filter((category) => !selectedCategoryIds.includes(category.id));
    return unselected.sort((a, b) => {
      const categoryNameA = a.name ?? '';
      const categoryNameB = b.name ?? '';
      return categoryNameA.localeCompare(categoryNameB);
    });
  }, [filteredCategories, selectedCategoryIds]);

  const itemClassName = useDropdownContext
    ? 'px-2 py-1.5'
    : 'px-3 py-2 cursor-pointer hover:bg-component-hover flex items-center gap-2 min-w-0';
  const labelClassName = useDropdownContext
    ? 'px-2 py-1.5 text-xs font-medium text-secondary'
    : 'px-3 py-1.5 text-xs font-medium text-secondary';
  const separatorClassName = useDropdownContext ? undefined : 'bg-border-light h-px mt-1';

  return (
    <>
      <div className="p-2">
        <div className="relative">
          <Search className="absolute left-2 top-1/2 -translate-y-1/2 size-4 text-secondary" />
          <input
            ref={searchInputRef}
            type="text"
            placeholder="Search"
            value={search}
            onChange={(e) => {
              onSearchChange(e.target.value);
              // Maintain focus after state update to prevent dropdown from stealing focus
              if (searchInputRef) {
                setTimeout(() => {
                  searchInputRef.current?.focus();
                }, 0);
              }
            }}
            onKeyDown={(e) => {
              // Prevent arrow keys from moving focus to dropdown items
              if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
                e.stopPropagation();
              }
              // Close dropdown on Enter
              if (e.key === 'Enter' && onEnterKeyDown) {
                e.preventDefault();
                onEnterKeyDown();
              }
            }}
            className="pl-8 h-10 w-full min-w-0 rounded-md border border-light bg-transparent px-3 py-1 text-dark shadow-xs transition-[color,box-shadow] outline-none focus-visible:border-primary focus-visible:ring-border-light focus-visible:ring-2"
          />
        </div>
      </div>
      {useDropdownContext ? <DropdownMenuSeparator /> : <div className={separatorClassName} />}
      <div className="max-h-[40vh] overflow-y-auto overflow-x-hidden">
        {selectedCategories.length > 0 && (
          <>
            {useDropdownContext ? (
              <DropdownMenuLabel className={labelClassName}>Selected</DropdownMenuLabel>
            ) : (
              <div className={labelClassName}>Selected</div>
            )}
            {selectedCategories.map((category) => {
              const content = (
                <div className="flex items-center gap-2 min-w-0">
                  <Checkbox checked={true} className="shrink-0" />
                  <CategoryIndicator hexColorCode={category.hexColorCode} emoji={category.emoji} />

                  <span className="truncate min-w-0">{category.name}</span>
                </div>
              );

              return useDropdownContext ? (
                <DropdownMenuItem
                  key={category.id}
                  onClick={() => {
                    onToggleCategory(category.id);
                  }}
                  className={itemClassName}
                >
                  {content}
                </DropdownMenuItem>
              ) : (
                <div
                  key={category.id}
                  onClick={() => {
                    onToggleCategory(category.id);
                  }}
                  className={itemClassName}
                >
                  {content}
                </div>
              );
            })}
            {unselectedCategories.length > 0 &&
              (useDropdownContext ? <DropdownMenuSeparator /> : <div className={separatorClassName} />)}
          </>
        )}
        {unselectedCategories.length > 0 && (
          <>
            {unselectedCategories.map((category) => {
              const content = (
                <div className="flex items-center gap-2 min-w-0">
                  <Checkbox checked={false} className="shrink-0" />
                  <CategoryIndicator hexColorCode={category.hexColorCode} emoji={category.emoji} />

                  <span className="truncate min-w-0">{category.name}</span>
                </div>
              );

              return useDropdownContext ? (
                <DropdownMenuItem
                  key={category.id}
                  onClick={() => {
                    onToggleCategory(category.id);
                  }}
                  className={itemClassName}
                >
                  {content}
                </DropdownMenuItem>
              ) : (
                <div
                  key={category.id}
                  onClick={() => {
                    onToggleCategory(category.id);
                  }}
                  className={itemClassName}
                >
                  {content}
                </div>
              );
            })}
          </>
        )}
        {selectedCategories.length === 0 && unselectedCategories.length === 0 && (
          <div className="px-2 py-4 text-center text-sm text-secondary">No options</div>
        )}
      </div>
      {selectedCategoryIds.length > 0 && (
        <>
          {useDropdownContext ? <DropdownMenuSeparator /> : <div className={separatorClassName} />}
          {useDropdownContext ? (
            <DropdownMenuItem onClick={onClearAll} className="px-2 py-1.5 text-primary focus:text-primary">
              <X className="size-4 mr-2 text-primary" />
              Clear all
            </DropdownMenuItem>
          ) : (
            <div
              onClick={onClearAll}
              className="px-3 py-2 cursor-pointer hover:bg-component-hover text-primary flex items-center gap-2"
            >
              <X className="size-4 text-primary" />
              Clear all
            </div>
          )}
        </>
      )}
    </>
  );
}

export function CategoriesFilterContent(props: CategoriesFilterContentProps) {
  const { useDropdownContext = true, ...rest } = props;
  return <CategoriesFilterContentBase {...rest} useDropdownContext={useDropdownContext} />;
}
